import { bound } from "@frui.ts/helpers/dist";
import { watchBusy } from "@frui.ts/screens/dist";
import { validate, validateAll } from "@frui.ts/validation";
import { KnownErrors, Modules } from "config";
import * as Sentry from "@sentry/react";
import type { interfaces } from "inversify";
import cloneDeep from "lodash/cloneDeep";
import { computed, observable } from "mobx";
import ChargesRepository from "repositories/chargesRepository";
import EventBus, { chargeRefundCandidateCreated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import EnumService from "services/enumService";
import AlertsViewModel from "viewModels/alertsViewModel";
import { serializeAccountNumber } from "utils/filters";
import CustomerRefundCandidateRequest, { CustomerRefundCandidateValidationRules } from "entities/customerRefundCandidateRequest";
import CountrySelectViewModel from "../../../countrySelectViewModel";
import ChargeResponse from "entities/chargeResponse";
import type ChargeChangeOrigin from "entities/chargeChangeOrigin";
import RefundCounterpartyOverridability from "entities/refundCounterpartyOverridability";
import { BaseRefundViewModel } from "./baseRefundViewModel";
import type ChargeChangesDuringValidityItemResponse from "entities/chargeChangesDuringValidityItemResponse";
import CustomerRefundCandidatePreviewResponse from "entities/customerRefundCandidatePreviewResponse";
import CustomerPartialRefundCandidatePreviewResponse from "entities/customerPartialRefundCandidatePreviewResponse";
import RefundPaymentType from "entities/refundPaymentType";
import TransferPaymentType from "entities/transferPaymentType";
import { formatCzechAccountNumber } from "@edaz/utils/bankAccount";
import CustomerRefundCandidatePreviewRefundPaymentResponse from "entities/customerRefundCandidatePreviewRefundPaymentResponse";
import PriceListService from "services/priceListService";
import type SearchChargesRequest from "entities/searchChargesRequest";

type RefundChargeError = {
  content?: {
    type: string;
  };
};

export default class RefundChargeViewModel extends BaseRefundViewModel<CustomerRefundCandidateRequest> {
  readonly validationRules = CustomerRefundCandidateValidationRules;
  @observable changes: ChargeChangesDuringValidityItemResponse[] = [];
  @observable previewPartialResponse?: CustomerRefundCandidatePreviewResponse;
  @observable refundPayment?: CustomerRefundCandidatePreviewRefundPaymentResponse;
  @observable isRefundProcessed: boolean = false;

  constructor(
    protected repository: ChargesRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    protected securityService: SecurityService,
    protected enumService: EnumService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    public alertsViewModel: AlertsViewModel,
    public priceListService: PriceListService,
    public chargeResponse: ChargeResponse,
    public filter: SearchChargesRequest
  ) {
    super(
      repository,
      localizationService,
      eventBus,
      notificationService,
      optionsService,
      securityService,
      enumService,
      countrySelectViewModelFactory,
      alertsViewModel,
      chargeResponse
    );
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  @computed
  get secondStep() {
    return this.previewPartialResponse;
  }

  @computed
  get paymentInformationRequired() {
    return (
      this.chargeResponse.permittedActions.refundation.counterpartyOverridability === RefundCounterpartyOverridability.Required
    );
  }

  @computed
  get paymentInformationOptional() {
    return (
      this.chargeResponse.permittedActions.refundation.counterpartyOverridability === RefundCounterpartyOverridability.Allowed
    );
  }

  @computed
  get paymentInformationDenied() {
    return (
      this.chargeResponse.permittedActions.refundation.counterpartyOverridability === RefundCounterpartyOverridability.Denied
    );
  }

  @computed
  get changesDuringValidity() {
    if (this.changes.length) {
      return this.changes[0];
    }

    return undefined;
  }

  @computed
  get newChargePrice() {
    return this.previewPartialResponse instanceof CustomerPartialRefundCandidatePreviewResponse
      ? this.previewPartialResponse.candidatePreview.newChargePrice
      : undefined;
  }

  @computed
  get rejectedReason() {
    return this.previewPartialResponse?.rejectionReason;
  }

  @computed
  get canBeSubmitted() {
    return this.previewPartialResponse?.canBeSubmitted;
  }

  @computed
  get informationText() {
    if (!this.previewPartialResponse) {
      return undefined;
    }

    const isRejected = !!this.previewPartialResponse?.rejectionReason;
    let informationText = "";

    if (isRejected) {
      informationText = this.translatePreview(`rejectedReason.${this.previewPartialResponse?.rejectionReason}`);
    } else {
      switch (this.previewPartialResponse?.candidatePreview?.refundPayment.paymentType) {
        case RefundPaymentType.DomesticTransfer:
          informationText = this.translatePreview("refundDomesticTransfer").replace(
            "%accountNumber%",
            formatCzechAccountNumber(
              this.previewPartialResponse?.candidatePreview?.refundPayment.transferPayment?.counterpartyBank?.bankAccount
            )
          );
          break;
        case RefundPaymentType.ForeignTransfer:
          informationText = this.translatePreview("refundForeignTransfer");
          break;
        case RefundPaymentType.SepaTransfer:
          informationText = this.translatePreview("refundSepaTransfer");
          break;
        case RefundPaymentType.PayGate:
          informationText = this.translatePreview("refundPayGate").replace(
            "%cardNumber%",
            this.previewPartialResponse?.candidatePreview?.refundPayment.payGatePayment?.cardNumber ?? ""
          );
          break;
      }
    }

    return informationText;
  }

  protected async loadDetail() {
    const entity = new CustomerRefundCandidateRequest();
    this.changes = (await this.repository.changesDuringValidity(this.chargeResponse.chargeId)).items;
    this.attachValidators(entity);
    return entity;
  }

  @bound
  onSubmit() {
    if (this.secondStep) {
      this.onSave();
    } else {
      this.previewRefund();
    }
  }

  @bound
  onBackClick() {
    this.previewPartialResponse = undefined;
    if (this.refundPayment?.isTransferredToSepa && this.item.transferType) {
      this.item.transferType = TransferPaymentType.Sepa;
    }
  }

  @bound
  @watchBusy
  private async previewRefund() {
    const isValid = this.includePaymentOptions ? validateAll([this.item, this.item.counterpartyBank]) : validate(this.item);
    if (!isValid) {
      return;
    }

    try {
      const data = cloneDeep(this.item);
      if (this.includePaymentOptions) {
        data.counterpartyBank.accountNumber = serializeAccountNumber(this.item.counterpartyBank.accountNumber);
      }
      if (data.counterpartyBank?.bankAccount && !data.counterpartyBank?.bankAccount?.bank) {
        data.counterpartyBank.bankAccount.bank = undefined;
      }
      const response = await this.repository.refundPreview(this.chargeResponse.chargeId, data);
      this.previewPartialResponse = response;
      this.refundPayment = response.candidatePreview.refundPayment;
    } catch (e) {
      const type = (e as RefundChargeError).content?.type;
      if (type === KnownErrors.REFUND_COUNTERPARTY_OVERRIDE_AFTER_SEPA_TRANSFER_INVALID) {
        this.item.transferType = TransferPaymentType.Sepa;
      }
      Sentry.captureException(e);
    }
  }

  @bound
  private priceListItem(charge: ChargeResponse) {
    return this.priceListService.priceListItemById(charge.priceListItemId);
  }

  @bound
  translateFuelType(charge: ChargeResponse) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === priceItem.fuelTypeId);
    if (!fuelType) {
      return;
    }
    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  @bound
  translateChargeType(charge: ChargeResponse) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const chargeType = this.enumService.values("charge_types").find(chargeType => chargeType.id === priceItem.chargeTypeId);
    if (!chargeType) {
      return;
    }
    return this.localizationService.translateEnum("chargeValidity", chargeType.validFor);
  }

  @bound
  @watchBusy
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async onSave() {
    if (!this.secondStep) {
      return this.previewRefund();
    }

    const isValid = this.includePaymentOptions ? validateAll([this.item, this.item.counterpartyBank]) : validate(this.item);

    if (isValid) {
      const data = cloneDeep(this.item);

      if (this.includePaymentOptions) {
        data.counterpartyBank.accountNumber = serializeAccountNumber(this.item.counterpartyBank.accountNumber);
      }
      if (data.counterpartyBank?.bankAccount && !data.counterpartyBank?.bankAccount?.bank) {
        data.counterpartyBank.bankAccount.bank = undefined;
      }

      let entity;

      if (this.refundPayment?.isTransferredToSepa && this.refundPayment?.transferPayment) {
        const transferredData = { ...data, ...this.refundPayment.transferPayment };
        transferredData.transferType = TransferPaymentType.Sepa;
        entity = await this.repository.refund(this.chargeResponse.chargeId, transferredData);
      } else {
        entity = await this.repository.refund(this.chargeResponse.chargeId, data);
      }
      const charge = await this.repository.getCharge(this.filter, this.chargeResponse.chargeId);
      if (charge.items[0]) {
        entity = charge.items[0];
      }
      // We set this alongside busyWatcher, because it takes some time for the modal to close after the refund response.
      // That way, even after successful refund, the user was able to re-submit the refund, which is not the desired effect.
      this.isRefundProcessed = true;

      if (entity) {
        this.eventBus.publish(chargeRefundCandidateCreated({ entity }));
      }
      this.notificationService.add({ title: this.translate("refunded"), type: NotificationType.SUCCESS });
      this.requestClose();
    }
  }

  @bound
  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `refund.${code}`, size);
  }

  translatePreview(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `refundPreview.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  translateCountryName(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? "";
  }

  translateChangeOrigin(chargeChangeOrigin: ChargeChangeOrigin) {
    return this.localizationService.translateEnum("chargeChangeOrigin", chargeChangeOrigin);
  }

  static Factory({ container }: interfaces.Context) {
    return (charge: ChargeResponse, filter: SearchChargesRequest) =>
      new RefundChargeViewModel(
        container.get(ChargesRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(OptionsService),
        container.get(SecurityService),
        container.get(EnumService),
        container.get(CountrySelectViewModel.Factory),
        container.get(AlertsViewModel),
        container.get(PriceListService),
        charge,
        filter
      );
  }
}
